import type React from "react";
import { useCallback, useEffect, useState } from "react";
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
  GridToolbar,
} from "@mui/x-data-grid";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { MoonLoader } from "react-spinners";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AlarmIcon from "@mui/icons-material/Alarm";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import BookOnlineIcon from "@mui/icons-material/BookOnline";
import {
  cancelBooking,
  getAvailableSlots,
  remindUser,
  updateBooking,
} from "../../apis/bookingApi";
import { getAllListings } from "../../apis/LaneCageApi";
import { useBooking } from "../../context/BookingContext";
import EditBooking from "./table/EditBooking";
import ViewBookingDialog from "./table/ViewBooking";
import { FinalBooking } from "../../schemas/bookings";
import { extractTimeFromDateString, formatDateToISO, passSelectedDate } from "../../hooks/Date";

interface BookingItemProps extends FinalBooking {
  userDetails: {
    name: string;
    email: string;
  };
  listingName: string;
}

interface TableProps {
  data: FinalBooking[];
  loading?: boolean;
  onStatusChange?: (
    id: string,
    newStatus: string,
    email?: string,
    listingName?: string,
    firstName?: string,
    lastName?: string,
    totalHours?: number,
    totalAmount?: number,
    bookingForDate?: string,
    startTime?: string,
    endTime?: string,
    laneOrCageId?: string,
    specialRequests?: string
  ) => void;
  onRefresh?: () => Promise<void> | void;
}

interface UpdateBookingData {
  laneOrCageId?: string;
  startTime?: string;
  endTime?: string;
  bookingForDate?: string;
  bookingStatus?: string | false;
  listingName?: string;
  specialRequests?: string;
}

const DataTable: React.FC<TableProps> = ({
  data,
  loading = false,
  onStatusChange,
  onRefresh,
}) => {
  const [selectedBooking, setSelectedBooking] =
    useState<BookingItemProps | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuBookingId, setMenuBookingId] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [, setListings] = useState<any[]>([]);
  const {
    setSelectedListing,
    setSelectedDate,
    setStartTime,
    setEndTime,
    checkAvailability,
  } = useBooking();

  const [editBookingDialogOpen, setEditBookingDialogOpen] = useState(false);
  const [editFormValues, setEditFormValues] = useState<UpdateBookingData>();
  const [availabilityStatus, setAvailabilityStatus] = useState<null | {
    available: boolean;
    booking?: any[];
  }>(null);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);

  // Add states for notification
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationType, setNotificationType] = useState<"success" | "error" | "info">(
    "success"
  );

  // Add new states for pricing and tax
  const [hourlyRate, setHourlyRate] = useState<number>(0);
  const [taxPercentage, setTaxPercentage] = useState<number>(14);
  const [updatedAmount, setUpdatedAmount] = useState<number>(0);
  const [finalAmountWithTax, setFinalAmountWithTax] = useState<number>(0);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: string) => {
    setAnchorEl(event.currentTarget);
    setMenuBookingId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuBookingId(null);
  };

  const handleViewDetails = (booking: BookingItemProps) => {
    setSelectedBooking(booking);
    setViewDialogOpen(true);
    handleMenuClose();
  };

  const handleEdit = (booking: BookingItemProps) => {
    setSelectedBooking(booking);
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleDelete = async (booking: BookingItemProps) => {
    setSelectedBooking(booking);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleReminder = async (booking: BookingItemProps) => {
    try {
      if (booking.bookingInfo.statusHistoryData.bookingStatus !== "cancelled") {
        const response = await remindUser(
          booking.listingName,
          booking.bookingInfo.userDetails.name,
          booking.bookingInfo.userDetails.email,
          new Date(booking.bookingInfo.startTime).toLocaleDateString([], {
            year: "numeric",
            month: "long",
            day: "numeric",
          }),
          booking.bookingInfo.startTime.toString(),
          booking.bookingInfo.endTime.toString()
        );

        if (response.message === "Reminder email sent successfully") {
          setNotificationMessage(
            `Reminder email sent to ${booking.bookingInfo.userDetails.name}`
          );
          setNotificationType("success");
          setNotificationOpen(true);
        } else {
          console.error("Failed to send reminder:", response.data);
          setNotificationMessage("Failed to send reminder email");
          setNotificationType("error");
          setNotificationOpen(true);
        }
      } else {
        setNotificationMessage("Booking is cancelled, no reminder sent.");
        setNotificationType("error");
        setNotificationOpen(true);
      }
    } catch (error) {
      console.error("Error sending reminder:", error);
      setNotificationMessage("Error sending reminder email");
      setNotificationType("error");
      setNotificationOpen(true);
    }
    handleMenuClose();
  };

  const handleCloseNotification = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setNotificationOpen(false);
  };

  const fetchListings = async () => {
    try {
      const response = await getAllListings();
      setListings(response.laneOrCages);
    } catch (error) {
      console.error("Error fetching listings:", error);
    }
  };

  useEffect(() => {
    fetchListings();
  }, []);

  // Function to calculate updated amount based on time changes
  const calculateUpdatedAmount = useCallback(
    (startTime: string, endTime: string): number => {
      if (!startTime || !endTime) return 0;

      const start = new Date(`2000-01-01T${startTime}`);
      const end = new Date(`2000-01-01T${endTime}`);

      if (end <= start) return 0;

      const diffInMs = end.getTime() - start.getTime();
      const diffInHours = diffInMs / (1000 * 60 * 60);

      return (
        diffInHours *
        (selectedBooking?.bookingInfo.laneOrCage.pricePerHour || hourlyRate)
      );
    },
    [hourlyRate, selectedBooking]
  );

  // Function to calculate final amount with tax
  const calculateFinalAmount = (baseAmount: number, tax: number): number => {
    return baseAmount + (baseAmount * tax) / 100;
  };

  // Update amounts when time or tax changes
  useEffect(() => {
    if (editFormValues?.startTime && editFormValues?.endTime) {
      const newAmount = calculateUpdatedAmount(
        editFormValues.startTime,
        editFormValues.endTime
      );
      setUpdatedAmount(newAmount);
      setFinalAmountWithTax(calculateFinalAmount(newAmount, taxPercentage));
    } else if (selectedBooking) {
      // Use original times if no changes
      const originalStartTime = extractTimeFromDateString(
        selectedBooking.bookingInfo.startTime.toString()
      );
      const originalEndTime = extractTimeFromDateString(
        selectedBooking.bookingInfo.endTime.toString()
      );
      const originalAmount = calculateUpdatedAmount(
        originalStartTime,
        originalEndTime
      );
      setUpdatedAmount(originalAmount);
      setFinalAmountWithTax(
        calculateFinalAmount(originalAmount, taxPercentage)
      );
    }
  }, [
    editFormValues?.startTime,
    editFormValues?.endTime,
    taxPercentage,
    hourlyRate,
    selectedBooking,
    calculateUpdatedAmount,
  ]);

  const deleteBooking = async (booking: BookingItemProps) => {
    setIsCancelling(true);
    try {
      const bookingId = booking.bookingInfo.id;
      const chargeId = booking.bookingInfo.payment.chargeId;
      const email = booking.bookingInfo.userDetails.email;
      const totalAmount = booking.bookingInfo.payment.totalAmount;
      const bookingDate = new Date(booking.bookingInfo.startTime);
      const currentDate = new Date();
      const timeDifference = bookingDate.getTime() - currentDate.getTime();
      const hoursDifference = timeDifference / (1000 * 60 * 60);
      const isWithin24Hours = hoursDifference < 24;
      const isAdminBooking =
        totalAmount === 0 || booking.bookingInfo.payment.chargeId?.startsWith("admin_");

      if (onStatusChange) {
        onStatusChange(
          bookingId,
          "cancelled",
          email,
          booking.listingName,
          booking.bookingInfo.userDetails.firstName,
          booking.bookingInfo.userDetails.lastName,
          booking.bookingInfo.payment.totalHours,
          booking.bookingInfo.payment.totalAmount,
          booking.bookingInfo.bookingForDate,
          new Date(booking.bookingInfo.startTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          new Date(booking.bookingInfo.endTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
        );
      }

      if (!isWithin24Hours && !isAdminBooking) {
        await cancelBooking(
          chargeId || "",
          email,
          booking.listingName,
          booking.bookingInfo.userDetails.name,
          booking.bookingInfo.userDetails.name,
          booking.bookingInfo.payment.totalHours,
          booking.bookingInfo.payment.totalAmount,
          booking.bookingInfo.bookingForDate,
          new Date(booking.bookingInfo.startTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          new Date(booking.bookingInfo.endTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
        );
      } else {
        console.log(
          "No refund issued. Reason:",
          isWithin24Hours
            ? "Less than 24 hours before start time"
            : "Admin booking (amount: $0)"
        );
      }

      setDeleteDialogOpen(false);

      // Show notification for booking cancellation
      setNotificationMessage(
        `Booking for ${booking.bookingInfo.userDetails.name} has been cancelled`
      );
      setNotificationType("success");
      setNotificationOpen(true);
    } catch (error) {
      console.error("Error cancelling booking:", error);
      setDeleteDialogOpen(false);

      // Show error notification
      setNotificationMessage("Error cancelling booking");
      setNotificationType("error");
      setNotificationOpen(true);
    } finally {
      setIsCancelling(false);
    }
  };

  // const handleStatusChange = (id: string, newStatus: string) => {
  //   if (onStatusChange && selectedBooking) {
  //     const email = selectedBooking.userDetails.email;
  //     onStatusChange(id, newStatus, email);

  //     // Show notification for status change
  //     setNotificationMessage(`Booking status updated to ${newStatus}`);
  //     setNotificationType("success");
  //     setNotificationOpen(true);
  //   }
  //   setEditDialogOpen(false);
  // };

  const handleStatusChange = async (id: string, newStatus: string) => {
    if (!selectedBooking) return;
    setIsStatusUpdating(true);
    try {
      if (onStatusChange) {
        const email = selectedBooking.bookingInfo.userDetails.email;
        await onStatusChange(id, newStatus, email); // Await if onStatusChange is async
        setNotificationMessage(`Booking status updated to ${newStatus}`);
        setNotificationType("success");
        setNotificationOpen(true);
      }
      setEditDialogOpen(false);
    } catch (error) {
      setNotificationMessage("Failed to update booking status");
      setNotificationType("error");
      setNotificationOpen(true);
    } finally {
      setIsStatusUpdating(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 400,
        }}
      >
        <MoonLoader color="#1976d2" size={40} />
        <Typography sx={{ ml: 2 }}>Loading bookings...</Typography>
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: 300,
          p: 3,
          backgroundColor: "#f9f9f9",
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No bookings found
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          There are no bookings available that match your criteria.
        </Typography>
      </Box>
    );
  }

  const handleCloseEditDialog = () => {
    setEditBookingDialogOpen(false);
    setAvailabilityStatus(null);
    // Reset amounts
    setUpdatedAmount(0);
    setFinalAmountWithTax(0);
  };

  // Replace the handleEditBooking function with this improved version
  const handleEditBooking = (booking: BookingItemProps) => {
    const bookingForDate = booking.bookingInfo.bookingForDate;
    const startTime = extractTimeFromDateString(
      booking.bookingInfo.startTime.toString()
    );
    const endTime = extractTimeFromDateString(
      booking.bookingInfo.endTime.toString()
    );

    setEditFormValues({
      laneOrCageId: booking.bookingInfo.laneOrCage.id,
      startTime: startTime,
      endTime: endTime,
      bookingForDate: bookingForDate,
      bookingStatus: booking.bookingInfo.statusHistoryData.bookingStatus,
      specialRequests: booking.bookingInfo.specialRequests || "",
    });

    // Also set these in the booking context
    setSelectedListing({ id: booking.bookingInfo.laneOrCage.id });
    setSelectedDate(bookingForDate.toString());
    setStartTime(startTime);
    setEndTime(endTime);

    setSelectedBooking(booking);
    setAvailabilityStatus(null); // Reset availability status

    // Initialize amounts with current booking
    const currentAmount = calculateUpdatedAmount(startTime, endTime);
    setUpdatedAmount(currentAmount);
    setFinalAmountWithTax(calculateFinalAmount(currentAmount, taxPercentage));

    setEditBookingDialogOpen(true);
    handleMenuClose();
  };

  // 4. Add function to save updated booking
  const hasChanges = () => {
    if (!selectedBooking || !editFormValues) return false;

    // Check if any field has changed
    const originalStartTime = extractTimeFromDateString(
      selectedBooking.bookingInfo.startTime.toString()
    );
    const originalEndTime = extractTimeFromDateString(
      selectedBooking.bookingInfo.endTime.toString()
    );
    // const originalDate = formatDateToISO(
    //   selectedBooking.bookingInfo.bookingForDate
    // );

    const originalDate = selectedBooking.bookingInfo.bookingForDate;

    return (
      editFormValues.laneOrCageId !==
        selectedBooking.bookingInfo.laneOrCage.id ||
      editFormValues.startTime !== originalStartTime ||
      editFormValues.endTime !== originalEndTime ||
      editFormValues.bookingForDate !== originalDate ||
      editFormValues.bookingStatus !==
        selectedBooking.bookingInfo.statusHistoryData.bookingStatus ||
      editFormValues.specialRequests !==
        (selectedBooking.bookingInfo.specialRequests || "")
    );
  };

  // const passSelectedDate = (date: string) => {
  //   const newDate = new Date(date);
  //   const year = newDate.getFullYear();
  //   const month = newDate.getMonth() + 1;
  //   const day = newDate.getDate();
  //   return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  // };


  // Replace the checkAvailabilityAndGetResult function with this improved version
  const checkAvailabilityAndGetResult = async () => {
    if (!selectedBooking) {
      setNotificationMessage("No booking selected");
      setNotificationType("error");
      setNotificationOpen(true);
      return false;
    }

    // Always use existing values if the form values are undefined or empty
    const laneOrCageId = selectedBooking.bookingInfo.laneOrCage.id;
    const bookingForDate =
      editFormValues?.bookingForDate ||
      passSelectedDate(selectedBooking.bookingInfo.bookingForDate, selectedBooking.bookingInfo.startTime.toString());
    const startTimeValue =
      editFormValues?.startTime ||
      extractTimeFromDateString(
        selectedBooking.bookingInfo.startTime.toString()
      );
    const endTimeValue =
      editFormValues?.endTime ||
      extractTimeFromDateString(selectedBooking.bookingInfo.endTime.toString());

    // Validate required fields - all should be present now
    if (!laneOrCageId) {
      setNotificationMessage("Please select a lane or cage");
      setNotificationType("error");
      setNotificationOpen(true);
      return false;
    }

    if (!bookingForDate) {
      setNotificationMessage("Please select a booking date");
      setNotificationType("error");
      setNotificationOpen(true);
      return false;
    }

    if (!startTimeValue || !endTimeValue) {
      setNotificationMessage("Please select start and end time");
      setNotificationType("error");
      setNotificationOpen(true);
      return false;
    }

    setIsCheckingAvailability(true);

    try {
      // Parse the date string components to create a date in local time
      const [year, month, day] = bookingForDate.split("-").map(Number);
      const date = new Date(year, month - 1, day); // month is 0-indexed in JS
      date.setHours(0, 0, 0, 0);

      const formattedDate = passSelectedDate(date.toString());

      const slots = await getAvailableSlots(formattedDate, laneOrCageId);
      const bookedSlots = slots.data?.bookedSlots || [];

      // Skip validation for the booking we're currently editing
      const currentBookingId = selectedBooking.bookingInfo.bookingId;
      const relevantSlots = bookedSlots.filter(
        (slot: any) => slot.bookingId !== currentBookingId
      );

      const timeToMinutes = (time: any) => {
        const [hours, minutes] = time.split(":").map(Number);
        return hours * 60 + minutes;
      };

      // Check if selected time overlaps with any booked slots
      const hasOverlap = relevantSlots.some((slot: any) => {
        const selectedStart = timeToMinutes(startTimeValue);
        const selectedEnd = timeToMinutes(endTimeValue);
        const slotStart = timeToMinutes(
          extractTimeFromDateString(slot.startTime)
        );
        const slotEnd = timeToMinutes(extractTimeFromDateString(slot.endTime));

        return (
          (selectedStart >= slotStart && selectedStart < slotEnd) || // Start time falls within a booked slot
          (selectedEnd > slotStart && selectedEnd <= slotEnd) || // End time falls within a booked slot
          (selectedStart <= slotStart && selectedEnd >= slotEnd) // Selected time completely encompasses a booked slot
        );
      });

      if (hasOverlap) {
        setAvailabilityStatus({ available: false });
        setNotificationMessage("Time slot conflicts with existing bookings");
        setNotificationType("error");
        setNotificationOpen(true);
        return false; // Return false to indicate not available
      } else {
        try {
          // Make sure booking context has latest values
          setSelectedListing({ id: laneOrCageId });
          setSelectedDate(formattedDate);
          setStartTime(startTimeValue);
          setEndTime(endTimeValue);

          const available = await checkAvailability();

          // Only set available true if the checkAvailability function also returns true
          if (available) {
            setAvailabilityStatus({ available: true });
            setNotificationMessage("Selected time slot is available!");
            setNotificationType("success");
            setNotificationOpen(true);
            return true; // Return true to indicate available
          } else {
            setAvailabilityStatus({ available: false });
            setNotificationMessage("This time slot is not available");
            setNotificationType("error");
            setNotificationOpen(true);
            return false; // Return false to indicate not available
          }
        } catch (error) {
          console.error("Error checking availability:", error);
          setNotificationMessage("Failed to check availability");
          setNotificationType("error");
          setNotificationOpen(true);
          return false; // Return false on error
        }
      }
    } catch (error) {
      console.error("Error checking availability:", error);
      setNotificationMessage("Failed to check availability");
      setNotificationType("error");
      setNotificationOpen(true);
      return false; // Return false on error
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  // Replace the saveUpdatedBooking function with this improved version
  const saveUpdatedBooking = async () => {
    if (!selectedBooking) return;

    try {
      // Initialize with the actual values from the original booking
      const originalStartTime = extractTimeFromDateString(
        selectedBooking.bookingInfo.startTime.toString()
      );
      const originalEndTime = extractTimeFromDateString(
        selectedBooking.bookingInfo.endTime.toString()
      );
      // const originalDate = formatDateToISO(
      //   selectedBooking.bookingInfo.bookingForDate
      // );

      const originalDate = selectedBooking.bookingInfo.bookingForDate;


      // Use either the changed values or the original values
      const bookingForDate = editFormValues?.bookingForDate || originalDate;
      const startTimeValue = editFormValues?.startTime || originalStartTime;
      const endTimeValue = editFormValues?.endTime || originalEndTime;
      const laneOrCageId = editFormValues?.laneOrCageId || selectedBooking.bookingInfo.laneOrCage.id;
      const bookingStatus =
        editFormValues?.bookingStatus ||
        selectedBooking.bookingInfo.statusHistoryData.bookingStatus ||
        "pending";
      const specialRequests =
        editFormValues?.specialRequests !== undefined
          ? editFormValues.specialRequests
          : (selectedBooking.bookingInfo.specialRequests || "");

          const bookingStatusChanged =
            bookingStatus !== selectedBooking.bookingInfo.statusHistoryData.bookingStatus;

      // Only check availability if we're changing location, date, or time
      const timeChanged =
        startTimeValue !== originalStartTime ||
        endTimeValue !== originalEndTime;
      const dateChanged = bookingForDate !== originalDate;
      const locationChanged =
        laneOrCageId !== selectedBooking.bookingInfo.laneOrCage.id;

      const specialRequestsChanged =
        specialRequests !== selectedBooking.bookingInfo.specialRequests;

        if (!specialRequestsChanged && !locationChanged && !dateChanged && !timeChanged && !bookingStatusChanged) {
          setNotificationMessage("No changes to save");
          setNotificationType("info");
          setNotificationOpen(true);
          return;
        }

      // If time, date, or location is changed, check availability
      if (timeChanged || dateChanged || locationChanged) {
        // Make sure the booking context has the latest values
        setSelectedListing({ id: laneOrCageId });
        if (dateChanged) {
          const [year, month, day] = bookingForDate.split("-").map(Number);
          const date = new Date(year, month - 1, day);
          date.setHours(0, 0, 0, 0);
          setSelectedDate(date.toString());
        }
        if (timeChanged) {
          setStartTime(startTimeValue);
          setEndTime(endTimeValue);
        }

        // Directly use the result of our availability check function
        const isAvailable = await checkAvailabilityAndGetResult();


        // If not available, stop here and don't save
        if (!isAvailable) {
          return;
        }
      }

      try {
        // Parse the date
        const [year, month, day] = bookingForDate.split("-").map(Number);
        const date = new Date(year, month - 1, day);
        date.setHours(0, 0, 0, 0);

        const dateToUse = bookingForDate;
        const start = new Date(`${dateToUse}T${startTimeValue}`);
        const end = new Date(`${dateToUse}T${endTimeValue}`);

        // Calculate total hours
        const totalHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);

        // Use the updated amount if time changed, otherwise keep original
        const newTotalAmount = timeChanged
          ? finalAmountWithTax
          : selectedBooking.bookingInfo.payment.totalAmount;

        // Prepare update data with all required fields
        const updateData: any = {
          bookingStatus: bookingStatusChanged ? bookingStatus : undefined,
          listingName: selectedBooking.listingName, // Keep original listing name
          totalHours: totalHours,
          totalAmount: newTotalAmount,
          bookingForDate: bookingForDate,
          startTime: startTimeValue,
          endTime: endTimeValue,
          laneOrCageId: laneOrCageId,
          specialRequests: specialRequestsChanged ? specialRequests : undefined,
          name: `${selectedBooking.bookingInfo.userDetails.firstName} ${selectedBooking.bookingInfo.userDetails.lastName}`,
        };

        // Call API to update the booking
        const response = await updateBooking(
          selectedBooking.bookingInfo.id,
          updateData.bookingStatus,
          selectedBooking.bookingInfo.userDetails.email,
          updateData.listingName,
          undefined, // user ID
          undefined, // total amount
          updateData.totalHours,
          updateData.totalAmount,
          updateData.bookingForDate,
          updateData.startTime,
          updateData.endTime,
          updateData.laneOrCageId,
          updateData.specialRequests
        );

        if (response.message === "Booking updated successfully") {
          // Avoid calling onStatusChange here to prevent duplicate update calls/emails
          if (onRefresh) await onRefresh();

          setEditBookingDialogOpen(false);
          setNotificationMessage("Booking updated successfully");
          setNotificationType("success");
          setNotificationOpen(true);
        } else {
          throw new Error("Failed to update booking");
        }
      } catch (error) {
        console.error("Error updating booking:", error);
        setNotificationMessage("Failed to update booking");
        setNotificationType("error");
        setNotificationOpen(true);
      }
    } catch (error) {
      console.error("Error updating booking:", error);
      setNotificationMessage("Failed to update booking");
      setNotificationType("error");
      setNotificationOpen(true);
    }
  };

  const handleRefund = async (
    chargeId: string,
    email: string,
    bookingId: string,
    booking: any
  ) => {
    if (chargeId.startsWith("ch_")) {
      const response = await cancelBooking(
        chargeId,
        email,
        booking.listingName,
        booking.bookingInfo.userDetails.firstName,
        booking.bookingInfo.userDetails.lastName,
        booking.bookingInfo.payment.totalHours,
        booking.bookingInfo.payment.totalAmount,
        booking.bookingInfo.bookingForDate,
        booking.bookingInfo.startTime.toString(),
        booking.bookingInfo.endTime.toString()
      );
      if (response.message === "Refund created successfully") {
        setNotificationMessage("Refund initiated successfully");
        setNotificationType("success");
        setNotificationOpen(true);
      } else {
        setNotificationMessage("Failed to initiate refund");
        setNotificationType("error");
        setNotificationOpen(true);
      }
      if (onStatusChange) {
        onStatusChange(
          bookingId,
          "refunded",
          email,
          booking.listingName,
          booking.bookingInfo.userDetails.firstName,
          booking.bookingInfo.userDetails.lastName,
          booking.bookingInfo.payment.totalHours,
          booking.bookingInfo.payment.totalAmount,
          booking.bookingInfo.bookingForDate,
          new Date(booking.bookingInfo.startTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          new Date(booking.bookingInfo.endTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
        );
      }
    } else if (chargeId.startsWith("admin_")) {
      alert("Cannot initiate refund for admin bookings");
    }
  };

  const getStatusChipColor = (status: string) => {
    switch (status) {
      case "confirmed":
        return "success";
      case "pending":
        return "warning";
      case "send_invoice":
        return "primary";
      case "pay_at_counter":
        return "warning";
      case "group_on":
        return "secondary";
      case "cancelled":
        return "error";
      case "invoice_sent":
        return "info";
      default:
        return "default";
    }
  };

  const rows = data.map((booking: FinalBooking, index: any) => {
    const startTime = new Date(booking.bookingInfo.startTime);
    const endTime = new Date(booking.bookingInfo.endTime);
    const bookingDate = new Date(booking.bookingInfo.bookingDate);
    // Use canonical bookingForDate from backend (YYYY-MM-DD) instead of endTime
    const bookingForDate = booking.bookingInfo.bookingForDate;

    return {
      id: booking.bookingInfo.id || index,
      _id: booking.bookingInfo.id,
      customer: booking.bookingInfo.userDetails.name,
      email: booking.bookingInfo.userDetails.email,
      listingName: booking.bookingInfo.laneOrCage.name,
      laneOrCageId: booking.bookingInfo.laneOrCage.id,
      startTime,
      endTime,
      totalAmount: booking.bookingInfo.payment.totalAmount,
      specialRequests: booking.bookingInfo.specialRequests || "None",
      bookingStatus: booking.bookingInfo.statusHistoryData.bookingStatus || "pending",
      bookingDate,
      bookingForDate,
      totalHours: booking.bookingInfo.payment.totalHours,
      originalBooking: booking,
    };
  });

  const columns: GridColDef[] = [
    {
      field: "customer",
      headerName: "Customer",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "listingName",
      headerName: "Lane/Cage",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "startTime",
      headerName: "Start Time",
      flex: 1,
      minWidth: 100,
      valueFormatter: (params: any) => {
        const date = params;
        return date instanceof Date
          ? date.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })
          : "Invalid date";
      },
    },
    {
      field: "endTime",
      headerName: "End Time",
      flex: 1,
      minWidth: 100,
      valueFormatter: (params: any) => {
        const date = params;
        return date instanceof Date
          ? date.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })
          : "Invalid date";
      },
    },
    {
      field: "totalAmount",
      headerName: "Amount",
      flex: 1,
      minWidth: 100,
      valueFormatter: (params: any) => {
        const amount = Number(params);
        return `$${Number.isFinite(amount) ? amount.toFixed(2) : "0.00"}`;
      },
    },
    {
      field: "bookingStatus",
      headerName: "Status",
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          size="small"
          color={getStatusChipColor(params.value) as any}
        />
      ),
    },
    {
      field: "bookingDate",
      headerName: "Booked On",
      flex: 1,
      minWidth: 120,
      valueFormatter: (params: any) => {
        const date = params;
        return date instanceof Date
          ? date.toLocaleDateString([], {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
          : "Invalid date";
      },
    },
    {
      field: "bookingForDate",
      headerName: "Booked For",
      flex: 1,
      minWidth: 120,
      valueFormatter: (params: any) => {
        const value = params as string;
        if (!value) return "";
        const d = new Date(`${value}T00:00:00`);
        return d.toLocaleDateString([], {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        const booking = params.row.originalBooking;
        return (
          <>
            <IconButton
              aria-label="more options"
              size="small"
              onClick={(event) => handleMenuOpen(event, params.id as string)}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl) && menuBookingId === params.id}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={() => handleViewDetails(booking)}>
                <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
                View Details
              </MenuItem>
              <MenuItem onClick={() => handleEditBooking(booking)}>
                <BookOnlineIcon fontSize="small" sx={{ mr: 1 }} />
                Edit Booking
              </MenuItem>
              <MenuItem onClick={() => handleEdit(booking)}>
                <EditIcon fontSize="small" sx={{ mr: 1 }} />
                Edit Status
              </MenuItem>
              {booking.bookingInfo.statusHistoryData.bookingStatus !== "cancelled" && (
                <MenuItem onClick={() => handleDelete(booking)}>
                  <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                  Cancel Booking
                </MenuItem>
              )}

              {booking.bookingInfo.statusHistoryData.bookingStatus !== "cancelled" && (
                <MenuItem onClick={() => handleReminder(booking)}>
                  <AlarmIcon fontSize="small" sx={{ mr: 1 }} />
                  Remind User
                </MenuItem>
              )}

              {booking.bookingInfo.statusHistoryData.bookingStatus !== "refunded" &&
              booking.bookingInfo.payment.chargeId?.startsWith("ch_") &&
              booking.bookingInfo.statusHistoryData.bookingStatus !== "cancelled" ? (
                <MenuItem
                  onClick={() =>
                    handleRefund(
                      booking.bookingInfo.payment.chargeId,
                      booking.bookingInfo.userDetails.email,
                      booking.bookingInfo.id,
                      booking
                    )
                  }
                >
                  <AttachMoneyIcon fontSize="small" sx={{ mr: 1 }} />
                  Initiate Refund
                </MenuItem>
              ) : (
                ""
              )}
            </Menu>
          </>
        );
      },
    },
  ];

  return (
    <>
      <Box sx={{ height: 550, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 },
            },
            sorting: {
              sortModel: [{ field: "bookingDate", sort: "desc" }],
            },
          }}
          getRowId={(row) => row.id}
          pageSizeOptions={[5, 10, 25]}
          density="standard"
          disableRowSelectionOnClick
          disableColumnFilter
          disableDensitySelector
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          sx={{
            border: "none",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f5f5f5",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#f8f9fa",
            },
          }}
        />
      </Box>

      <Snackbar
        open={notificationOpen}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notificationType}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {notificationMessage}
        </Alert>
      </Snackbar>

      <ViewBookingDialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        selectedBooking={selectedBooking}
        onEditStatus={handleEdit}
      />

      <EditBooking
        open={editBookingDialogOpen}
        onClose={handleCloseEditDialog}
        selectedBooking={selectedBooking}
        editFormValues={editFormValues}
        setEditFormValues={setEditFormValues}
        setSelectedDate={setSelectedDate}
        setStartTime={setStartTime}
        setEndTime={setEndTime}
        isCheckingAvailability={isCheckingAvailability}
        checkAvailabilityAndGetResult={checkAvailabilityAndGetResult}
        availabilityStatus={availabilityStatus}
        hourlyRate={hourlyRate}
        setHourlyRate={setHourlyRate}
        taxPercentage={taxPercentage}
        setTaxPercentage={setTaxPercentage}
        updatedAmount={updatedAmount}
        finalAmountWithTax={finalAmountWithTax}
        saveUpdatedBooking={saveUpdatedBooking}
        handleCloseEditDialog={handleCloseEditDialog}
        hasChanges={hasChanges}
      />

      {/* Edit Status Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Update Booking Status</DialogTitle>
        <DialogContent>
          {selectedBooking && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                Current status:
                <Chip
                  label={selectedBooking.bookingInfo.statusHistoryData.bookingStatus}
                  size="small"
                  color={
                    getStatusChipColor(
                        selectedBooking.bookingInfo.statusHistoryData.bookingStatus || "pending"
                    ) as any
                  }
                  sx={{ ml: 1 }}
                />
              </Typography>

              <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
                Select new status:
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                sx={{ flexWrap: "wrap", gap: 1 }}
              >
                {isStatusUpdating ? (
                  <CircularProgress size={28} sx={{ m: 1 }} />
                ) : (
                  [
                    "Confirmed",
                    "Pay_At_Counter",
                    "Cancelled",
                    "Invoice_Sent",
                    "Send_Invoice",
                  ].map((status) => (
                    <Chip
                      key={status}
                      label={status}
                      clickable
                      disabled={isStatusUpdating}
                      color={getStatusChipColor(status) as any}
                      onClick={() =>
                        handleStatusChange(
                          selectedBooking.bookingInfo.id,
                          status
                        )
                      }
                      variant={
                        selectedBooking.bookingInfo.statusHistoryData.bookingStatus === status
                          ? "filled"
                          : "outlined"
                      }
                    />
                  ))
                )}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Cancel Booking</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to cancel this booking?
          </Typography>
          {selectedBooking && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2">
                Customer: {selectedBooking.bookingInfo.userDetails.name}
              </Typography>
              <Typography variant="body2">
                Booking Date:{" "}
                {passSelectedDate(selectedBooking.bookingInfo.startTime)}
              </Typography>
              <Typography variant="body2">
                Lane/Cage: {selectedBooking.bookingInfo.laneOrCage.name}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={isCancelling}
          >
            No, Keep It
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteBooking(selectedBooking as BookingItemProps)}
            disabled={isCancelling}
            startIcon={isCancelling ? <CircularProgress size={20} /> : null}
          >
            {isCancelling ? "Cancelling..." : "Yes, Cancel Booking"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DataTable;
